/**
 * LandingPage Component (Admin Portal)
 * 
 * Main dashboard for store admins/owners showing recent orders, quick actions,
 * and navigation to key features (Reports, Menu, Team, Store Setup).
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onStartNew - Callback to create new order
 * @param {Function} props.onViewReports - Callback to view reports
 * @param {Function} props.onViewMenuManagement - Callback to view menu management
 * @param {Function} props.onViewTeamManagement - Callback to view team management
 * @param {Function} props.onViewStoreSetup - Callback to view store setup
 * @param {Function} props.onEditOrder - Callback to edit order
 * @param {Function} props.onViewOrder - Callback to view order details
 * @param {Function} props.onPasswordChange - Callback for password change
 * @param {Function} props.onUserPasswordChange - Callback for user password change
 * @param {Function} props.onLogout - Callback to logout
 * @param {Function} props.onCopyOrder - Callback to copy order
 * @param {Object} props.currentStore - Current store data
 * @param {Object} props.currentUser - Current logged-in user
 * @param {Object} props.subscriptionStatus - Subscription status
 * @param {Function} props.onUpgrade - Callback to upgrade subscription
 * @param {Function} props.onManageSubscription - Callback to manage subscription
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Plus, BarChart3, Store, Users, LogOut, Eye, Edit, X, CreditCard, Utensils, FileText, Sparkles, MoreVertical, Trash2, Copy, Search } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/constants';
import { formatMonDD, getImageUrl } from '../../utils/formatters';

// Import subscription components
import TrialBanner from '../subscription/TrialBanner';

// Import password change modals
import { AdminPasswordChange, UserPasswordChange } from '../shared/PasswordChangeModals';

// Import PaymentModal for recording payments
import PaymentModal from '../orders/PaymentModal';

// Import FloatingContactButton from App.js (we'll need to extract it)
// For now, let's create a placeholder to avoid runtime errors
function FloatingContactButton() {
  return null; // Placeholder - will be properly implemented
}

function LandingPage({ onStartNew, onViewReports, onViewMenuManagement, onViewTeamManagement, onViewStoreSetup, onViewAuditLogs, onViewCustomerManagement, onEditOrder, onViewOrder, onPasswordChange, onUserPasswordChange, onLogout, onCopyOrder, currentStore, currentUser, subscriptionStatus, onUpgrade, onManageSubscription }) {
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showUserPasswordChange, setShowUserPasswordChange] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [recentOrders, setRecentOrders] = useState([]);
  
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
  
  // Check if user is staff (limited access)
  const isStaff = currentUser?.role === 'store_staff';
  const isStoreAdmin = currentUser?.role === 'store_admin';
  const isStoreOwner = currentUser?.role === 'store_owner';
  
  // Refs for dropdown click-outside detection
  const settingsRef = React.useRef(null);
  const tasksRef = React.useRef(null);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [sortBy, setSortBy] = useState('event_date');  // Changed from 'created_at' to 'event_date'
  const [sortOrder, setSortOrder] = useState('desc');
  const [discountDisplayMode, setDiscountDisplayMode] = useState('amount'); // 'amount' or 'percentage'
  const [bulkEditMode, setBulkEditMode] = useState(false);
  
  // Dropdown menu states
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showTasksMenu, setShowTasksMenu] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  
  // Click outside to close dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettingsMenu(false);
      }
      if (tasksRef.current && !tasksRef.current.contains(event.target)) {
        setShowTasksMenu(false);
      }
    }
    
    if (showSettingsMenu || showTasksMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSettingsMenu, showTasksMenu]);
  
  // Bulk selection states
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);

  // Search handler - client-side filtering like Staff page
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setIsSearchMode(false);
      setSearchResults([]);
      return;
    }
    
    try {
      setSearching(true);
      const query = searchQuery.toLowerCase().trim();
      
      // Filter from recentOrders (client-side filtering like Staff page)
      const filtered = recentOrders.filter(order => {
        const orderPhone = String(order.customer_info?.phone || '').trim().toLowerCase();
        const orderName = String(order.customer_info?.name || '').trim().toLowerCase();
        const orderId = String(order.id || '').trim().toLowerCase();
        const invoiceCode = String(order.invoice_code || '').trim().toLowerCase();
        
        return orderPhone.includes(query) || 
               orderName.includes(query) || 
               orderId.includes(query) ||
               invoiceCode.includes(query);
      });
      
      setSearchResults(filtered);
      setIsSearchMode(true);
      
      if (filtered.length === 0) {
        toast.info('No orders found matching your search');
      }
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearchMode(false);
  };

  // Get orders to display (search results or recent orders)
  const getDisplayOrders = () => {
    return isSearchMode ? searchResults : recentOrders;
  };

  useEffect(() => {
    // Only fetch when we have a stable currentStore that matches the logged-in user's storeId
    if (!currentStore) {
      setRecentOrders([]);
      return;
    }
    const storeId = currentStore.storeId || currentStore.id;
    if (!storeId) return;
    if (currentUser?.storeId && currentUser.storeId !== storeId) return;
    fetchRecentOrders(storeId);
  }, [currentStore?.storeId, currentStore?.id, currentUser?.storeId]);

  const fetchRecentOrders = async (storeId) => {
    try {
      setLoadingOrders(true);
      console.log('Fetching orders for storeId:', storeId);
      // Fetch orders relevant to the owner dashboard: any order whose
      // event date is upcoming or within the last 30 days. The backend
      // filters by `event_date` when date_field=event_date is passed.
      const response = await axios.get(`${API}/orders?days=30&date_field=event_date&store_id=${storeId}`);
      console.log('Admin: Fetched recent orders:', response.data.length);
      setRecentOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoadingOrders(false);
    }
  };

  // Bulk selection functions
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedOrders([]);
      setSelectAll(false);
    } else {
      setSelectedOrders(getDisplayOrders().map(order => order.id));
      setSelectAll(true);
    }
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      const newSelected = prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId];
      
      setSelectAll(newSelected.length === recentOrders.length);
      return newSelected;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedOrders.length === 0) {
      toast.error('Please select orders to delete');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedOrders.length} selected order${selectedOrders.length > 1 ? 's' : ''}? This cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoadingOrders(true);
      
      // Delete orders one by one
      const deletePromises = selectedOrders.map(orderId => 
        axios.delete(`${API}/orders/${orderId}`)
      );
      
      await Promise.all(deletePromises);
      
      toast.success(`Successfully deleted ${selectedOrders.length} order${selectedOrders.length > 1 ? 's' : ''}`);
      
      // Reset selection and refresh orders
      setSelectedOrders([]);
      setSelectAll(false);
      
      // Refresh the list with proper storeId
      const storeId = currentStore?.storeId || currentStore?.id;
      if (storeId) {
        await fetchRecentOrders(storeId);
      }
      
    } catch (error) {
      console.error('Failed to delete orders:', error);
      toast.error('Failed to delete some orders. Please try again.');
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleDelete = async (orderId, orderName) => {
    if (!window.confirm(`Are you sure you want to delete order for ${orderName}? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success('Order deleted successfully');
      setSelectedOrders(selectedOrders.filter(id => id !== orderId));
      
      // Refresh the list with proper storeId
      const storeId = currentStore?.storeId || currentStore?.id;
      if (storeId) {
        fetchRecentOrders(storeId);
      }
    } catch (error) {
      console.error('Failed to delete order:', error);
      toast.error('Failed to delete order');
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Helper function to get delivery/pickup time from order
  const getDeliveryTime = (order) => {
    // First try to get time from customer_info.event_time (newer format)
    if (order.customer_info?.event_time) {
      const deliveryTime = order.customer_info.event_time;
      // Check if already in 12h format
      if (deliveryTime.includes('AM') || deliveryTime.includes('PM')) {
        return deliveryTime;
      }
      // Convert 24h format to 12h format (e.g., "18:00" -> "6:00 PM")
      const [hours, minutes] = deliveryTime.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    }
    
    // Try day_meal_selections (current format used in order creation)
    const daySelections = order.customer_info?.day_meal_selections || order.day_meal_selections || order.event_days;
    if (daySelections && daySelections.length > 0) {
      const firstDay = daySelections[0];
      if (firstDay.meal_times && firstDay.meal_times.length > 0) {
        const deliveryTime = firstDay.meal_times[0].delivery_time;
        if (deliveryTime) {
          // Convert 24h format to 12h format (e.g., "18:00" -> "6:00 PM")
          const [hours, minutes] = deliveryTime.split(':');
          const hour = parseInt(hours, 10);
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const hour12 = hour % 12 || 12;
          return `${hour12}:${minutes} ${ampm}`;
        }
      }
    }
    return null;
  };

  // Helper function to get full datetime for sorting (combines date + time)
  const getEventDateTime = (order) => {
    const dateStr = order.customer_info?.start_date || '9999-12-31';
    const timeStr = getDeliveryTimeRaw(order); // Get time in 24h format for sorting
    
    if (timeStr) {
      // Combine date and time for proper chronological sorting
      return new Date(`${dateStr.split('T')[0]}T${timeStr}:00`);
    }
    return new Date(dateStr);
  };

  // Helper to get raw time in 24h format for sorting
  const getDeliveryTimeRaw = (order) => {
    // First try customer_info.event_time
    if (order.customer_info?.event_time) {
      const time = order.customer_info.event_time;
      // If already in 24h format, return as-is
      if (!time.includes('AM') && !time.includes('PM')) {
        return time;
      }
      // Convert 12h to 24h for sorting
      const [timePart, period] = time.split(' ');
      let [hours, minutes] = timePart.split(':');
      hours = parseInt(hours, 10);
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    }
    
    // Try day_meal_selections
    const daySelections = order.customer_info?.day_meal_selections || order.day_meal_selections || order.event_days;
    if (daySelections && daySelections.length > 0) {
      const firstDay = daySelections[0];
      if (firstDay.meal_times && firstDay.meal_times.length > 0) {
        return firstDay.meal_times[0].delivery_time || null;
      }
    }
    return null;
  };

  const getSortedOrders = () => {
    const ordersToSort = getDisplayOrders();
    const sorted = [...ordersToSort].sort((a, b) => {
      let aVal, bVal;
      
      switch(sortBy) {
        case 'created_at':
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
          break;
        case 'event_date':
          // Use combined date + time for chronological sorting
          aVal = getEventDateTime(a);
          bVal = getEventDateTime(b);
          break;
        case 'customer_name':
          aVal = a.customer_info.name.toLowerCase();
          bVal = b.customer_info.name.toLowerCase();
          break;
        case 'phone':
          aVal = a.customer_info.phone || '';
          bVal = b.customer_info.phone || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'amount':
          aVal = a.total_amount;
          bVal = b.total_amount;
          break;
        case 'discount':
          aVal = a.discount_amount || 0;
          bVal = b.discount_amount || 0;
          break;
        case 'balance':
          aVal = a.balance_amount || (a.total_amount - (a.advance_payment || 0) - (a.discount_amount || 0));
          bVal = b.balance_amount || (b.total_amount - (b.advance_payment || 0) - (b.discount_amount || 0));
          break;
        case 'payment':
          // Sort by paid status (paid = 0, unpaid = 1)
          aVal = (a.balance_amount || (a.total_amount - (a.advance_payment || 0) - (a.discount_amount || 0))) === 0 ? 0 : 1;
          bVal = (b.balance_amount || (b.total_amount - (b.advance_payment || 0) - (b.discount_amount || 0))) === 0 ? 0 : 1;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bawarchi-background-muted)' }}>
      {/* Trial Banner */}
      <TrialBanner subscriptionStatus={subscriptionStatus} onUpgrade={onUpgrade} />
      
      {/* Sticky Navigation */}
      <div className="nav-bawarchi">
        <div className="nav-brand flex items-center gap-2">
          {currentStore?.logoUrl && (
            <img src={getImageUrl(currentStore.logoUrl)} alt="Store Logo" className="h-16 w-auto" />
          )}
          <div className="flex flex-col">
            <span className="font-semibold">{currentStore?.name || 'CaterLog'}</span>
            {currentStore?.tagline && (
              <span className="text-sm text-gray-600">{currentStore.tagline}</span>
            )}
          </div>
        </div>
        <div className="nav-links flex items-center gap-2">
          {/* Settings Dropdown - Only for Store Owners */}
          {isStoreOwner && (
            <div className="relative" ref={settingsRef}>
              <button
                onClick={() => {
                  setShowSettingsMenu(!showSettingsMenu);
                  if (!showSettingsMenu) setShowTasksMenu(false);
                }}
                className="btn-bawarchi-secondary flex items-center gap-2"
                style={{padding: '8px 16px', minHeight: '40px'}}
              >
                <span className="text-lg">⚙️</span>
                Settings
              </button>
              {showSettingsMenu && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 min-w-[220px] z-50">
                  <button
                    onClick={() => {
                      onViewStoreSetup();
                      setShowSettingsMenu(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 rounded-t-lg"
                  >
                    <Store className="w-4 h-4" />
                    Store Setup
                  </button>
                  <button
                    onClick={() => {
                      onViewTeamManagement();
                      setShowSettingsMenu(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Users className="w-4 h-4" />
                    Team Management
                  </button>
                  {onViewAuditLogs && (
                    <button
                      data-testid="settings-audit-trail-btn"
                      onClick={() => {
                        onViewAuditLogs();
                        setShowSettingsMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 border-t"
                    >
                      <FileText className="w-4 h-4" />
                      Audit Trail
                    </button>
                  )}
                  {/* Change Password - Temporarily Hidden 
                  <button
                    onClick={() => {
                      setShowChangePasswordModal(true);
                      setShowSettingsMenu(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 border-t"
                  >
                    <Edit className="w-4 h-4" />
                    Change Password
                  </button>
                  */}
                  <button
                    onClick={() => {
                      onManageSubscription();
                      setShowSettingsMenu(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 border-t rounded-b-lg"
                  >
                    <CreditCard className="w-4 h-4" />
                    Subscription Management
                  </button>
                </div>
              )}
            </div>
          )}
          
          <button
            onClick={onLogout}
            className="btn-bawarchi-secondary"
            style={{ padding: '8px 20px', minHeight: '40px' }}
          >
            <LogOut className="w-4 h-4 mr-2 inline" />
            Logout
          </button>
        </div>
      </div>

      <div className="page-container">
        <div className="page-header text-center">
          <h1 className="page-title">Admin Portal</h1>
          <p className="page-subtitle">Full Access Management</p>
        </div>
        
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Password Change Modals */}
          {showPasswordChange && (
            <div className="mb-6">
              <AdminPasswordChange
                currentPassword={onPasswordChange.currentPassword}
                onPasswordChange={onPasswordChange.onChange}
                onClose={() => setShowPasswordChange(false)}
              />
            </div>
          )}
          
          {showUserPasswordChange && (
            <div className="mb-6">
              <UserPasswordChange
                currentPassword={onUserPasswordChange.currentPassword}
                onPasswordChange={onUserPasswordChange.onChange}
                onClose={() => setShowUserPasswordChange(false)}
              />
            </div>
          )}

          {/* Catering Action Buttons - Clean Modern UI (Center Aligned) */}
          <div className="flex flex-wrap gap-3 mb-8 justify-center">
            <button
              onClick={onStartNew}
              className="btn-bawarchi-primary flex items-center gap-2"
              style={{padding: '10px 20px', minHeight: '44px', fontSize: '15px'}}
            >
              <Plus className="w-4 h-4" />
              Create New Order
            </button>
            <button
              onClick={onViewReports}
              className="btn-bawarchi-secondary flex items-center gap-2"
              style={{padding: '10px 20px', minHeight: '44px', fontSize: '15px'}}
            >
              <BarChart3 className="w-4 h-4" />
              View Reports
            </button>
            {/* Menu Management - Only for Store Owners */}
            {isStoreOwner && (
              <button
                onClick={onViewMenuManagement}
                className="btn-bawarchi-secondary flex items-center gap-2"
                style={{padding: '10px 20px', minHeight: '44px', fontSize: '15px'}}
              >
                <Utensils className="w-4 h-4" />
                Menu Management
              </button>
            )}
            {/* Customer Management - For Store Owners and Admins */}
            {(isStoreOwner || isStoreAdmin) && onViewCustomerManagement && (
              <button
                onClick={onViewCustomerManagement}
                data-testid="customer-management-btn"
                className="btn-bawarchi-secondary flex items-center gap-2"
                style={{padding: '10px 20px', minHeight: '44px', fontSize: '15px'}}
              >
                <Users className="w-4 h-4" />
                Customer Management
              </button>
            )}
          </div>

          {/* Search Orders */}
          <div className="card-bawarchi mb-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <input
                  type="text"
                  className="input-bawarchi pr-10 w-full"
                  placeholder="Search by phone, customer name, or order ID..."
                  value={searchQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchQuery(value);
                    // Auto-clear search results when search query is cleared (like Staff page)
                    if (!value.trim()) {
                      setSearchResults([]);
                      setIsSearchMode(false);
                    }
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              </div>
              <button
                onClick={handleSearch}
                disabled={searching || !searchQuery.trim()}
                className="btn-bawarchi-primary"
                style={{ padding: '8px 20px', minHeight: '40px' }}
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>

          {/* Recent Orders */}
          <div className="card-bawarchi">
            <div style={{ marginBottom: 'var(--spacing-6)' }}>
              <div className="flex justify-between items-center">
                <h2 className="card-bawarchi-title" style={{ marginBottom: 0 }}>
                  {isSearchMode ? `Search Results (${searchResults.length})` : 'Upcoming & Recent Orders (Last 30 Days)'}
                </h2>
                <div className="flex gap-2">
                  {bulkEditMode && selectedOrders.length > 0 && (
                    <button
                      onClick={handleBulkDelete}
                      className="btn-bawarchi-destructive"
                      style={{ padding: '8px 16px', minHeight: '40px', fontSize: '14px' }}
                    >
                      <X className="w-4 h-4 mr-2 inline" />
                      Delete Selected ({selectedOrders.length})
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setBulkEditMode(!bulkEditMode);
                      setSelectedOrders([]);
                    }}
                    className={bulkEditMode ? "btn-bawarchi-secondary" : "btn-bawarchi-primary"}
                    style={{ padding: '8px 16px', minHeight: '40px', fontSize: '14px' }}
                  >
                    {bulkEditMode ? 'Cancel Bulk Edit' : 'Bulk Edit'}
                  </button>
                </div>
              </div>
            </div>
            <div>
              {loadingOrders ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading orders...</p>
                </div>
              ) : getDisplayOrders().length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">{isSearchMode ? 'No orders found matching your search' : 'No upcoming or recent orders found'}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table-bawarchi w-full text-xs table-fixed">
                    <thead>
                      <tr>
                        {bulkEditMode && (
                          <th className="text-center w-8 p-1">
                            <input
                              type="checkbox"
                              checked={selectAll}
                              onChange={handleSelectAll}
                              className="rounded"
                              style={{ accentColor: 'var(--bawarchi-primary)' }}
                          />
                          </th>
                        )}
                        <th className="cursor-pointer hover:bg-gray-100 p-1 w-24" onClick={() => handleSort('customer_name')}>
                          Name {sortBy === 'customer_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="cursor-pointer hover:bg-gray-100 p-1 w-24" onClick={() => handleSort('phone')}>
                          Phone {sortBy === 'phone' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="cursor-pointer hover:bg-gray-100 p-1 w-14" onClick={() => handleSort('created_at')}>
                          Made {sortBy === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="cursor-pointer hover:bg-gray-100 p-1 w-28" onClick={() => handleSort('event_date')}>
                          Event {sortBy === 'event_date' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="cursor-pointer hover:bg-gray-100 p-1 w-20" onClick={() => handleSort('status')}>
                          Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="text-right cursor-pointer hover:bg-gray-100 p-1 w-16" onClick={() => handleSort('amount')}>
                          Total {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="text-right p-1 w-16">
                          <div className="flex items-center justify-end gap-0.5">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDiscountDisplayMode(discountDisplayMode === 'amount' ? 'percentage' : 'amount');
                              }}
                              className="text-xs w-4 h-4 rounded border border-orange-300 bg-orange-50 hover:bg-orange-100"
                              title={`Switch to ${discountDisplayMode === 'amount' ? 'percentage' : 'amount'} view`}
                            >
                              {discountDisplayMode === 'amount' ? '$' : '%'}
                            </button>
                            <span className="cursor-pointer" onClick={() => handleSort('discount')}>
                              Disc {sortBy === 'discount' && (sortOrder === 'asc' ? '↑' : '↓')}
                            </span>
                          </div>
                        </th>
                        <th className="text-right cursor-pointer hover:bg-gray-100 p-1 w-14" onClick={() => handleSort('advance')}>
                          Adv {sortBy === 'advance' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="text-right cursor-pointer hover:bg-gray-100 p-1 w-16" onClick={() => handleSort('balance')}>
                          Bal {sortBy === 'balance' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="text-center cursor-pointer hover:bg-gray-100 p-1 w-14" onClick={() => handleSort('payment')}>
                          Paid {sortBy === 'payment' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="text-center p-1 w-8">⋮</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSortedOrders().map((order) => {
                        const balance = order.balance_amount !== undefined ? order.balance_amount : (order.total_amount - (order.advance_payment || 0) - (order.discount_amount || 0));
                        const isPaid = balance <= 0.01; // Allow for floating point tolerance
                        const isQuote = order.status?.toLowerCase() === 'quote';
                        
                        return (
                        <tr key={order.id} className={selectedOrders.includes(order.id) ? 'bg-soft-primary' : ''}>
                          {bulkEditMode && (
                            <td className="text-center p-1">
                              <input
                                type="checkbox"
                                checked={selectedOrders.includes(order.id)}
                                onChange={() => handleSelectOrder(order.id)}
                                className="rounded"
                                style={{ accentColor: 'var(--bawarchi-primary)' }}
                              />
                            </td>
                          )}
                          <td className="p-1 truncate" title={order.customer_info.name}>
                            <div className="flex items-center gap-1">
                              <span className="truncate">{order.customer_info.name}</span>
                              {order.is_multi_order && order.sub_order_label && (
                                <span 
                                  className="inline-flex items-center px-1 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 rounded whitespace-nowrap" 
                                  title={`Sub-order: ${order.sub_order_label} | Group: ${order.group_id?.slice(0, 8)}...`}
                                >
                                  {order.sub_order_label.split(' - ')[1]?.slice(0, 3) || 'Sub'}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-1">{order.customer_info.phone}</td>
                          <td className="p-1">{formatMonDD(order.created_at)}</td>
                          <td className="p-1 whitespace-nowrap">
                            {formatMonDD(order.customer_info.start_date)}
                            {getDeliveryTime(order) && (
                              <span className="text-orange-600 ml-0.5">{getDeliveryTime(order)}</span>
                            )}
                          </td>
                          <td className="p-1">
                            {isQuote ? (
                              <button
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  
                                  // Optimistic update - immediately update UI
                                  setRecentOrders(prevOrders => 
                                    prevOrders.map(o => 
                                      o.id === order.id ? { ...o, status: 'confirmed' } : o
                                    )
                                  );
                                  
                                  try {
                                    await axios.put(`${API}/orders/${order.id}/confirm-quote`);
                                    toast.success('Order confirmed successfully!');
                                  } catch (error) {
                                    console.error('Failed to confirm order:', error);
                                    toast.error(error.response?.data?.detail || 'Failed to confirm order');
                                    
                                    // Revert optimistic update on error
                                    setRecentOrders(prevOrders => 
                                      prevOrders.map(o => 
                                        o.id === order.id ? { ...o, status: 'quote' } : o
                                      )
                                    );
                                  }
                                }}
                                className="px-1 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 hover:bg-yellow-200 cursor-pointer"
                                title="Click to confirm"
                              >
                                Quote↻
                              </button>
                            ) : (
                              <span className={`text-xs px-1 py-0.5 rounded ${order.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                {order.status}
                              </span>
                            )}
                          </td>
                          <td className="text-right font-semibold p-1">
                            ${((order.subtotal || 0) + (order.service_fee || 0)).toFixed(0)}
                          </td>
                          <td className="text-right text-orange-600 p-1">
                            {(() => {
                              if (discountDisplayMode === 'percentage') {
                                const subtotal = order.total_amount + (order.discount_amount || 0);
                                const percentage = subtotal > 0 ? ((order.discount_amount || 0) / subtotal) * 100 : 0;
                                return percentage > 0 ? `-${percentage.toFixed(0)}%` : '0';
                              } else {
                                return order.discount_amount > 0 ? `-$${order.discount_amount.toFixed(0)}` : '$0';
                              }
                            })()}
                          </td>
                          <td className="text-right text-blue-600 p-1">
                            ${(order.advance_payment || 0).toFixed(0)}
                          </td>
                          <td className={`text-right font-bold p-1 ${isPaid ? 'text-green-600' : 'text-red-600'}`}>
                            ${balance.toFixed(0)}
                          </td>
                          <td className="text-center p-1">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedOrderForPayment(order);
                                setShowPaymentModal(true);
                              }}
                              className={`px-1 py-0.5 text-xs rounded ${
                                isPaid 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}
                              data-testid={`payment-btn-${order.id}`}
                            >
                              {isPaid ? '✓' : 'DUE'}
                            </button>
                          </td>
                          <td className="p-1 text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="h-6 w-6 flex items-center justify-center hover:bg-gray-100 rounded">
                                  <MoreVertical className="h-4 w-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => {
                                  const sortedList = getSortedOrders();
                                  const currentIndex = sortedList.findIndex(o => o.id === order.id);
                                  onViewOrder(order.id, sortedList, currentIndex);
                                }}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onEditOrder(order.id)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onCopyOrder(order.id)}>
                                  <Copy className="mr-2 h-4 w-4" />
                                  Copy
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(order.id, order.customer_info.name)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Subscription Management Modal - Use showUpgradeModal instead */}

      {/* Customer Follow-Up Modal */}
      {showFollowUpModal && (
        <Dialog open={showFollowUpModal} onOpenChange={setShowFollowUpModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Customer Follow-Up
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Coming Soon!</h3>
                <p className="text-gray-600 mb-4">
                  Automated customer follow-up and feedback collection is coming in our next update. Stay tuned!
                </p>
                <div className="space-y-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowFollowUpModal(false)}
                    className="w-full"
                  >
                    Got it
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Payment Modal */}
      <PaymentModal
        open={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedOrderForPayment(null);
        }}
        order={selectedOrderForPayment}
        currentUser={currentUser}
        viewMode={selectedOrderForPayment && (selectedOrderForPayment.balance_amount <= 0 || selectedOrderForPayment.status === 'paid')}
        onPaymentRecorded={async () => {
          // Refresh orders after payment recorded
          const storeId = currentStore?.storeId || currentStore?.id;
          if (storeId) {
            await fetchRecentOrders(storeId);
          }
        }}
      />
      
      {/* Powered by Caterlog Footer */}
      <div className="text-center py-6 text-sm text-gray-500">
        Powered by <span className="font-semibold text-orange-600">Caterlog</span>
      </div>
      
      {/* Floating Contact Button */}
      <FloatingContactButton />
    </div>
  );
}

// Menu Management Page Component

export default LandingPage;
